/* eslint-disable no-plusplus */
import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import { downloadBytes } from 'utils/downloadBytes';
import { EnvelopeWithStatus } from 'types/Envelope';

import Button from 'modules/_shared/components/Buttons';
import { addAlert } from 'modules/_shared/redux/alertActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import {
  downloadEnvelope,
  sendEnvelopeReminder,
} from 'modules/_shared/redux/envelopeActions';
import { getEnvelopeSignatureId } from 'modules/envelopes/utils/getEnvelopeSignatureId';
import { EnvelopeStatus } from 'modules/envelopes/components/EnvelopesTable/envelopeStatus';

type EnvelopeDetailsActionsProps = {
  envelope: EnvelopeWithStatus;
  status: EnvelopeStatus;
  refetchEnvelope: () => void;
};

const statusWithoutActions = [
  EnvelopeStatus.VOIDED,
  EnvelopeStatus.EXPIRED,
  EnvelopeStatus.DECLINED,
  EnvelopeStatus.DELETED,
];
const statusWithoutUpdate = [EnvelopeStatus.COMPLETED];
const statusWithoutReminder = [EnvelopeStatus.COMPLETED];

const EnvelopeDetailsActions: FC<EnvelopeDetailsActionsProps> = ({
  envelope,
  status,
  refetchEnvelope,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const allActionsDisabled = statusWithoutActions.includes(status);

  const isDownloadButtonDisabled = allActionsDisabled;
  const isUpdateStatusButtonDisabled =
    allActionsDisabled || statusWithoutUpdate.includes(status);
  const isReminderButtonDisabled =
    allActionsDisabled || statusWithoutReminder.includes(status);

  const handleDownloadDocument = async () => {
    setIsLoading(true);

    try {
      const response = (await downloadEnvelope(envelope.envelopeId)) as {
        status: string;
        data: string;
      };

      if (response.status !== 'success') {
        throw new Error('Error downloading envelope');
      }

      await downloadBytes(response.data, envelope.document.name);

      dispatch(addAlert(alertBodyTypes.ENVELOPE_DOWNLOADED_SUCCESSFULLY));
    } catch (error) {
      dispatch(addAlert(alertBodyTypes.ERROR_DOWNLOADING_ENVELOPE));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    setIsLoading(true);

    try {
      await refetchEnvelope();

      dispatch(addAlert(alertBodyTypes.ENVELOPE_STATUS_REFRESHED_SUCCESSFULLY));
    } catch (error) {
      dispatch(addAlert(alertBodyTypes.ERROR_REFRESHING_ENVELOPE_STATUS));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEnvelope = async () => {
    setIsLoading(true);
    if (!envelope) return;

    try {
      const envelopeId = envelope._id || '';
      const envelopeSignatureId = getEnvelopeSignatureId(envelope);

      await sendEnvelopeReminder(envelopeId, envelopeSignatureId);

      dispatch(addAlert(alertBodyTypes.ENVELOPE_REMINDER_SENT_SUCCESSFULLY));
    } catch (error) {
      dispatch(addAlert(alertBodyTypes.ERROR_SENDING_ENVELOPE_REMINDER));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-100 d-flex flex-column flex-md-row flex-wrap justify-content-between mt-1">
      <Button
        disabled={isDownloadButtonDisabled}
        variant="primary"
        onClick={handleDownloadDocument}
        className="mb-2 mb-md-0 me-md-2"
      >
        <em className="icon ni ni-download mr-2" />
        {t('Download PDF')}
      </Button>

      <Button
        disabled={isUpdateStatusButtonDisabled}
        variant="primary"
        outlineStyle
        onClick={handleUpdateStatus}
        className="mb-2 mb-md-0 me-md-2"
      >
        <em className="icon ni ni-update mr-2" />
        {t('Refresh Status')}
      </Button>

      <Button
        disabled={isReminderButtonDisabled}
        variant="primary"
        outlineStyle
        onClick={handleResendEnvelope}
        className="mb-0"
      >
        <em className="icon ni ni-send mr-2" />
        {t('Send Reminder')}
      </Button>

      {isLoading && <CustomLoading />}
    </div>
  );
};

export default EnvelopeDetailsActions;
